import React, { useState, useEffect } from 'react';

import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';

import drawerContentConfig from './Content'

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import icn_drawer_collapse from "assets/images/icons/icn_drawer_collapse.svg";
import icn_drawer_collapse_hover from "assets/images/icons/icn_drawer_collapse_hover.svg";
import icn_drawer_expand from "assets/images/icons/icn_drawer_expand.svg";
import icn_drawer_expand_hover from "assets/images/icons/icn_drawer_expand_hover.svg";

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  boxShadow: "none",
  zIndex: 5,
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const DrawerField = ({
  contentKey,
  open,
  handleClose,
  children,
  anchor = 'right',
  width = 560,
  commonContent = null,
  contentProps = {},
  stepper = false
}) => {

  const [pageStack, setPageStack] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [hover, setHover] = useState({
    collapse: false,
    expand: false,
  })

  function handleHover(key, isHover) {
    setHover((prev) => ({ ...prev, [key]: isHover }));
  };

  function pushPage(newContent, commonContentName, commonContentProps = {}) {
    const newStack = pageStack.slice(0, currentIndex + 1);
    if (commonContentName) {
      const CommonContent = drawerContentConfig[commonContentName];

      newStack.push({ Component: CommonContent, props: commonContentProps });
    } else {
      newStack.push({
        Component: newContent,
        props: {},
      });
    }
    setPageStack(newStack);
    setCurrentIndex(newStack.length - 1);
  }

  function handlePrev() {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  function handleNext() {
    if (currentIndex < pageStack.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const renderContent = (page) => {
    if (!page) return null;

    const { Component } = page;
    const isInitialPage = pageStack[0] === page;
    const props = isInitialPage && commonContent ? contentProps : page.props;

    if (Component === React.Fragment) {
      return React.cloneElement(children, {
        ...props,
        contentKey,
        pushPage,
      });
    }

    return <Component {...props} contentKey={contentKey} pushPage={pushPage} />;
  };

  useEffect(() => {
    if (!open) return;

    const initialPage = commonContent
      ? {
        Component: drawerContentConfig[commonContent],
        props: contentProps,
      }
      : {
        Component: React.Fragment,
        props: children?.props ?? {},
      };

    setPageStack([initialPage]);
    setCurrentIndex(0);
  }, [open, contentKey]);

  const drawerContent = (
    <Box
      sx={
        anchor === 'top' || anchor === 'bottom'
          ? { height: width, width: 'auto' }
          : { width }
      }
    >
      <Grid container spacing={4} direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={10}>
          {
            stepper && (
              <Box display="flex">
                <StyledIconButton
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  onMouseEnter={() => handleHover('collapse', true)}
                  onMouseLeave={() => handleHover('collapse', false)}
                >
                  <img src={hover.collapse ? icn_drawer_collapse_hover : icn_drawer_collapse} alt="icn_drawer_collapse" />
                </StyledIconButton>
                <StyledIconButton
                  onClick={handleNext}
                  disabled={currentIndex === pageStack.length - 1}
                  onMouseEnter={() => handleHover('expand', true)}
                  onMouseLeave={() => handleHover('expand', false)}
                >
                  <img src={hover.expand ? icn_drawer_expand_hover : icn_drawer_expand} alt="icn_drawer_expand" />
                </StyledIconButton>
              </Box>
            )}
        </Grid>
        <Grid item xs={2}>
          <Box style={{ justifySelf: 'end' }}>
            <StyledIconButton onClick={handleClose}>
              <CloseIcon />
            </StyledIconButton>
          </Box>
        </Grid>
      </Grid>
      {renderContent(pageStack[currentIndex])}
    </Box>
  );

  return (
    <Drawer
      variant="persistent"
      anchor={anchor}
      open={open}
      onClose={() => { }}
      ModalProps={{
        hideBackdrop: false,
        disableEscapeKeyDown: true,
        onBackdropClick: (e) => {
          e.stopPropagation();
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default DrawerField;